import React, { useState, useEffect } from 'react';
import { X, Globe, Loader2, Send, CheckCircle, ShieldCheck } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import toast from 'react-hot-toast';
import {
  createNetworkDraft,
  submitPropertyPost,
} from '../utils/networkApi';
import type { NetworkPost } from '../utils/networkApi';
import MoneyInput from './MoneyInput';

interface SharePropertyWizardProps {
  formId: string;
  address: { city: string; neighborhood?: string; street: string; houseNumber: string };
  onClose: () => void;
  onShared?: () => void;
}

type Step = 'loading' | 'form' | 'creating' | 'submitting' | 'done';

const FLOOR_OPTIONS = [
  { value: '', label: 'לא צוין' },
  { value: 'low', label: 'קומות נמוכות' },
  { value: 'mid', label: 'קומות ביניים' },
  { value: 'high', label: 'קומות גבוהות' },
];

const ENTRY_OPTIONS = [
  { value: '', label: 'לא צוין' },
  { value: 'flexible', label: 'גמיש' },
  { value: 'immediate', label: 'מיידי' },
  { value: '3months', label: 'עד 3 חודשים' },
  { value: '6months', label: 'עד 6 חודשים' },
];

function toNum(v: number | null | undefined): number | undefined {
  if (v === null || v === undefined) return undefined;
  return v;
}

function formatNum(v: number | null | undefined): string {
  if (v === null || v === undefined) return '';
  return v.toString();
}

export default function SharePropertyWizard({ formId, address, onClose, onShared }: SharePropertyWizardProps) {
  const [step, setStep] = useState<Step>('loading');
  const [draft, setDraft] = useState<NetworkPost | null>(null);

  // Editable Network fields (prefilled from Firestore property)
  const [priceMin, setPriceMin] = useState<number | undefined>(undefined);
  const [priceMax, setPriceMax] = useState<number | undefined>(undefined);
  const [sizeMin, setSizeMin] = useState('');
  const [sizeMax, setSizeMax] = useState('');
  const [roomsMin, setRoomsMin] = useState('');
  const [roomsMax, setRoomsMax] = useState('');
  const [floorGroup, setFloorGroup] = useState('');
  const [entryTiming, setEntryTiming] = useState('');
  const [publicDescription, setPublicDescription] = useState('');
  const [isExclusive, setIsExclusive] = useState(false);

  const inputClass = 'w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors';
  const labelClass = 'block text-xs font-medium text-gray-400 mb-1';

  // Fetch the full Firestore property document to prefill Network fields.
  // The wizard needs price, size, rooms from the property to prefill.
  // No address/owner/office data is sent to the backend.
  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const formDoc = await getDoc(doc(db, 'forms', formId));
        if (!formDoc.exists()) {
          toast.error('נכס לא נמצא');
          onClose();
          return;
        }
        const data = formDoc.data();
        const price = data.property?.price;
        const size = data.property?.size;
        const rooms = data.property?.rooms;

        setPriceMin(toNum(price));
        setPriceMax(toNum(price));
        setSizeMin(formatNum(size));
        setSizeMax(formatNum(size));
        setRoomsMin(formatNum(rooms));
        setRoomsMax(formatNum(rooms));
        setStep('form');
      } catch {
        toast.error('שגיאה בטעינת נתוני הנכס');
        onClose();
      }
    };
    fetchProperty();
  }, [formId]);

  // Step: form → creating draft
  const handleCreateDraft = async () => {
    if (!isExclusive) {
      toast.error('נדרש לאשר בלעדיות לפני יצירת טיוטה');
      return;
    }

    const pMin = priceMin ?? null;
    const pMax = priceMax ?? null;
    const sMin = sizeMin ? parseFloat(sizeMin) : null;
    const sMax = sizeMax ? parseFloat(sizeMax) : null;
    const rMin = roomsMin ? parseFloat(roomsMin) : null;
    const rMax = roomsMax ? parseFloat(roomsMax) : null;

    if (pMin === null && pMax === null) { toast.error('חובה למלא לפחות ערך מחיר אחד'); return; }
    if (sMin === null && sMax === null) { toast.error('חובה למלא לפחות ערך שטח אחד'); return; }
    if (rMin === null && rMax === null) { toast.error('חובה למלא לפחות ערך חדרים אחד'); return; }

    if (pMin !== null && pMax !== null && pMin > pMax) { toast.error('מחיר מינימלי לא יכול להיות גבוה ממקסימלי'); return; }
    if (sMin !== null && sMax !== null && sMin > sMax) { toast.error('שטח מינימלי לא יכול להיות גבוה ממקסימלי'); return; }
    if (rMin !== null && rMax !== null && rMin > rMax) { toast.error('חדרים מינימלי לא יכול להיות גבוה ממקסימלי'); return; }

    setStep('creating');
    try {
      const result = await createNetworkDraft(formId, {
        priceMin: pMin,
        priceMax: pMax,
        sizeMin: sMin,
        sizeMax: sMax,
        roomsMin: rMin,
        roomsMax: rMax,
        floorGroup: floorGroup || null,
        entryTiming: entryTiming || null,
        publicDescription: publicDescription.trim() || null,
        isExclusive: true,
      });

      if (result.success && result.data?.post) {
        setDraft(result.data.post);
        setStep('submitting');
        // Immediately submit the draft for publishing
        const submitResult = await submitPropertyPost(result.data.post.id);
        if (submitResult.success) {
          setStep('done');
          onShared?.();
        } else {
          toast.error(submitResult.message || 'הטיוטה נוצרה אך הפרסום נכשל');
          setStep('form');
        }
      } else {
        toast.error(result.message || 'שגיאה ביצירת הטיוטה');
        setStep('form');
      }
    } catch {
      toast.error('שגיאת תקשורת');
      setStep('form');
    }
  };

  const addressLine = `${address.city}${address.neighborhood ? ` · ${address.neighborhood}` : ''}`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[70] p-4" dir="rtl">
      <div className="bg-gray-800 rounded-xl max-w-lg w-full border border-gray-700 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="p-4 border-b border-gray-700 flex justify-between items-center shrink-0">
          <div className="flex items-center gap-2">
            <Globe size={18} className="text-blue-400" />
            <h3 className="text-base font-bold text-white">שיתוף ב-RV Network</h3>
          </div>
          {step === 'form' && (
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
              <X size={20} />
            </button>
          )}
        </div>

        {/* Step: loading property data */}
        {step === 'loading' && (
          <div className="p-10 flex flex-col items-center gap-4">
            <Loader2 size={32} className="animate-spin text-blue-400" />
            <p className="text-sm text-gray-400">טוען נתוני נכס...</p>
          </div>
        )}

        {/* Step: form (editable Network fields) */}
        {step === 'form' && (
          <>
            <div className="p-4 overflow-y-auto flex-1 space-y-4">
              <div className="bg-gray-700/50 rounded-lg p-3 text-sm text-gray-200 border border-gray-600">
                {addressLine}
              </div>

              <p className="text-xs text-gray-400">
                הנתונים להלן מולאו מטופס הנכס. ניתן לערוך לפני הפרסום ברשת.
              </p>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>מחיר מינימום (₪)</label>
                  <MoneyInput value={priceMin} onChange={setPriceMin} className={inputClass} placeholder="מינימום" />
                </div>
                <div>
                  <label className={labelClass}>מחיר מקסימום (₪)</label>
                  <MoneyInput value={priceMax} onChange={setPriceMax} className={inputClass} placeholder="מקסימום" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>שטח מינימום (מ"ר)</label>
                  <input type="number" value={sizeMin} onChange={e => setSizeMin(e.target.value)} className={inputClass} placeholder="מינימום" />
                </div>
                <div>
                  <label className={labelClass}>שטח מקסימום (מ"ר)</label>
                  <input type="number" value={sizeMax} onChange={e => setSizeMax(e.target.value)} className={inputClass} placeholder="מקסימום" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>חדרים מינימום</label>
                  <input type="number" value={roomsMin} onChange={e => setRoomsMin(e.target.value)} className={inputClass} step="0.5" placeholder="מינימום" />
                </div>
                <div>
                  <label className={labelClass}>חדרים מקסימום</label>
                  <input type="number" value={roomsMax} onChange={e => setRoomsMax(e.target.value)} className={inputClass} step="0.5" placeholder="מקסימום" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>קבוצת קומה</label>
                  <select value={floorGroup} onChange={e => setFloorGroup(e.target.value)} className={inputClass}>
                    {FLOOR_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>מועד כניסה</label>
                  <select value={entryTiming} onChange={e => setEntryTiming(e.target.value)} className={inputClass}>
                    {ENTRY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className={labelClass}>תיאור ציבורי</label>
                <textarea
                  value={publicDescription}
                  onChange={e => setPublicDescription(e.target.value)}
                  className={`${inputClass} resize-none`}
                  rows={3}
                  maxLength={500}
                  placeholder="תיאור קצר שיוצג לסוכנים אחרים ברשת"
                />
              </div>

              {/* Exclusivity checkbox — mandatory, button disabled until checked */}
              <label className="flex items-start gap-2 cursor-pointer bg-gray-700/30 rounded-lg p-3 border border-gray-600">
                <input
                  type="checkbox"
                  checked={isExclusive}
                  onChange={e => setIsExclusive(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-blue-500 shrink-0"
                />
                <span className="text-xs text-gray-300 leading-relaxed">
                  אני מאשר שלמשרד קיימת בלעדיות תקפה על הנכס
                </span>
              </label>
            </div>

            <div className="p-4 border-t border-gray-700 flex justify-end gap-2 shrink-0">
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors">
                ביטול
              </button>
              <button
                onClick={handleCreateDraft}
                disabled={!isExclusive}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ShieldCheck size={15} />
                צור טיוטה ופרסם
              </button>
            </div>
          </>
        )}

        {/* Step: creating draft */}
        {step === 'creating' && (
          <div className="p-10 flex flex-col items-center gap-4">
            <Loader2 size={32} className="animate-spin text-blue-400" />
            <p className="text-sm text-gray-400">יוצר טיוטה ברשת...</p>
          </div>
        )}

        {/* Step: submitting */}
        {step === 'submitting' && (
          <div className="p-10 flex flex-col items-center gap-4">
            <Loader2 size={32} className="animate-spin text-blue-400" />
            <p className="text-sm text-gray-400">מפרסם את הנכס ב-RV Network...</p>
          </div>
        )}

        {/* Step: done */}
        {step === 'done' && (
          <div className="p-8 flex flex-col items-center gap-4 text-center">
            <CheckCircle size={40} className="text-green-400" />
            <div>
              <p className="text-base font-semibold text-white">הנכס פורסם בהצלחה!</p>
              <p className="text-sm text-gray-400 mt-1">הנכס פעיל ברשת וגלוי למשרדים אחרים.</p>
            </div>
            <button
              onClick={onClose}
              className="mt-2 px-5 py-2 text-sm font-medium bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              סגור
            </button>
          </div>
        )}
      </div>
    </div>
  );
}